<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import MediaEditModal from "@/components/modals/media-edit-modal.vue";
import MediaPickerModal from "@/components/media/MediaPickerModal.vue";
import MediaPreview from "@/components/media/MediaPreview.vue";
import MediaGrid from "@/components/media/MediaGrid.vue";
import MediaFilters from "./partials/media-filters.vue";
import mediaApi from "@/helpers/media/media-api.js";
import Swal from "sweetalert2";

const defaultFilters = () => ({
  name: "",
  type: null,
  extension: "",
  only_images: false,
  start_date: "",
  end_date: "",
});

export default {
  page: {
    title: "Biblioteca de medios",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    MediaEditModal,
    MediaPickerModal,
    MediaPreview,
    MediaGrid,
    MediaFilters,
  },
  data() {
    return {
      title: "Biblioteca de medios",
      tableData: [],
      tableLoading: false,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100],
      totalRows: 0,
      viewMode: "table",
      mediaEdit: null,
      currentGridItem: null,
      filters: defaultFilters(),
      fields: [
        { key: "preview", label: "", thStyle: { width: "70px" } },
        { key: "name", label: "Nombre", sortable: false },
        { key: "type", label: "Tipo", sortable: false },
        { key: "extension", label: "Ext.", sortable: false },
        { key: "size", label: "Tamaño", sortable: false },
        { key: "creator", label: "Subido por", sortable: false },
        { key: "created_at", label: "Fecha", sortable: false },
        { key: "action", label: "", tdClass: "custom-table-action", sortable: false },
      ],
    };
  },
  mounted() {
    this.loadDataFromApi();
  },
  methods: {
    formatSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    buildParams() {
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        sort_by: "updated_at",
        sort: "desc",
      };
      if (this.filters.name) params.name = this.filters.name;
      if (this.filters.type) params.type = this.filters.type;
      if (this.filters.extension) params.extension = this.filters.extension;
      if (this.filters.only_images) params.only_images = 1;
      if (this.filters.start_date) params.start_date = this.filters.start_date;
      if (this.filters.end_date) params.end_date = this.filters.end_date;
      return params;
    },
    async loadDataFromApi() {
      this.tableLoading = true;
      try {
        const response = await mediaApi.list(this.buildParams());
        this.tableData = response?.data?.data || [];
        const meta = response?.data?.meta || {};
        this.totalRows = meta.total != null ? Number(meta.total) : 0;
      } catch (error) {
        console.error("Error loading media:", error);
        this.tableData = [];
        this.totalRows = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadDataFromApi();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.loadDataFromApi();
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadDataFromApi();
    },
    resetFilters() {
      this.filters = defaultFilters();
      this.currentPage = 1;
      this.loadDataFromApi();
    },
    openUpload() {
      this.$bvModal.show("ModalMediaUpload");
    },
    onUploaded() {
      this.loadDataFromApi();
    },
    editMedia(item) {
      this.mediaEdit = { ...item };
      this.$bvModal.show("ModalMediaEdit");
    },
    onGridSelect(item) {
      this.currentGridItem = item;
      this.editMedia(item);
    },
    async copyUrl(item) {
      if (!item?.url) return;
      await navigator.clipboard.writeText(item.url);
      this.$bvToast.toast("URL copiada al portapapeles", {
        title: "Multimedia",
        variant: "success",
        solid: true,
        autoHideDelay: 2000,
      });
    },
    async deleteMedia(item) {
      const result = await Swal.fire({
        title: "¿Eliminar archivo?",
        text: `"${item.name}" se eliminará permanentemente del servidor.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#f46a6a",
      });

      if (!result.isConfirmed) return;

      try {
        await mediaApi.delete(item.id);
        this.loadDataFromApi();
        Swal.fire({
          icon: "success",
          title: "Eliminado",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error deleting media:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar el archivo.",
        });
      }
    },
    onEditSuccess() {
      this.loadDataFromApi();
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title">
      <template v-slot:header-button>
        <div class="d-flex align-items-center">
          <b-button-group class="mr-2">
            <b-button
              size="sm"
              :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
              @click="viewMode = 'table'"
            >
              <i class="ri-list-check"></i>
            </b-button>
            <b-button
              size="sm"
              :variant="viewMode === 'grid' ? 'primary' : 'outline-primary'"
              @click="viewMode = 'grid'"
            >
              <i class="ri-grid-line"></i>
            </b-button>
          </b-button-group>
          <button type="button" class="btn btn-primary" @click="openUpload">
            <i class="fa fa-plus"></i> Subir archivos
          </button>
        </div>
      </template>
    </PageHeader>

    <MediaFilters
      :filters="filters"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <div class="card">
      <div class="card-body">
        <div v-if="viewMode === 'table'">
          <div class="row mb-3">
            <div class="col">
              <div class="d-flex align-items-center">
                <label class="mb-0 mr-2">Mostrar</label>
                <b-form-select
                  v-model="perPage"
                  size="sm"
                  :options="pageOptions"
                  class="w-auto"
                  @change="onPerPageChange"
                />
                <span class="ml-2 text-muted">registros</span>
              </div>
            </div>
          </div>

          <b-table
            :items="tableData"
            :fields="fields"
            :busy="tableLoading"
            responsive
            show-empty
            empty-text="No hay archivos multimedia."
          >
            <template #table-busy>
              <div class="text-center text-muted my-4">Cargando...</div>
            </template>

            <template #cell(preview)="data">
              <MediaPreview :item="data.item" size="48px" />
            </template>

            <template #cell(size)="data">
              {{ formatSize(data.item.size) }}
            </template>

            <template #cell(creator)="data">
              {{ data.item.creator?.name || "—" }}
            </template>

            <template #cell(action)="data">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary mr-1"
                title="Editar"
                @click="editMedia(data.item)"
              >
                <i class="ri-edit-line"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary mr-1"
                title="Copiar URL"
                @click="copyUrl(data.item)"
              >
                <i class="ri-file-copy-line"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                title="Eliminar"
                @click="deleteMedia(data.item)"
              >
                <i class="ri-delete-bin-line"></i>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-muted small">
              Total: {{ totalRows }} archivo(s)
            </span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="right"
              @change="onPageChange"
            />
          </div>
        </div>

        <div v-else>
          <div v-if="tableLoading" class="text-center py-4 text-muted">Cargando...</div>
          <MediaGrid
            v-else
            :items="tableData"
            :selectable="false"
            @select="onGridSelect"
          />
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-muted small">Total: {{ totalRows }} archivo(s)</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="right"
              @change="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <MediaEditModal :media="mediaEdit" @success="onEditSuccess" />

    <MediaPickerModal
      modal-id="ModalMediaUpload"
      :single="false"
      :upload-only="true"
      @uploaded="onUploaded"
    />
  </Layout>
</template>
